import { fromZonedTime } from "date-fns-tz";
import { laserCareerCourseKey } from "@/lib/sellout-2026";
import { addVatPence } from "@/utils/payment-plan";
import { UK_TIMEZONE } from "@/utils/uk-datetime";
import { HAIRDRESSING_OFFER_ID } from "@/lib/hairdressing-offer";

export const SELLOUT_OFFER_STORAGE_KEY = "cc.selloutOffer";
export const SELLOUT_PAGE_PATH = "/2026-dates/";
export const SELLOUT_BANNER_KEY = "cc.guidance.banner.2026-dates";
export const OFFER_COURSE_INC_VAT_PENCE = 20_000;
export const SELLOUT_OFFERS_CLOSES_AT = "2026-10-31T23:59:59";
export const SELLOUT_OFFERS_CLOSES_LABEL = "Closes Saturday 31 October, 23:59 UK.";
export const SELLOUT_OFFERS_CLOSES_SHORT = "Closes Sat 31 Oct, 23:59";
export const LASER_CARBON_KEY = "carbon-2026-11-27";

export const SELLOUT_OFFER_IDS = [
  "filler-pass",
  "regenerative-pass",
  "full-day-pass",
  "skin-tech-duo",
  "level-7-bls",
  "trainer-week",
  "laser-career-bundle",
] as const;

export const BOOKABLE_OFFER_IDS = [...SELLOUT_OFFER_IDS, HAIRDRESSING_OFFER_ID] as const;
export type SelloutOfferId = (typeof BOOKABLE_OFFER_IDS)[number];

export type SelloutOfferLine = {
  eventId: string;
  title: string;
  catalogueIncVatPence: number;
  chargedIncVatPence: number | null;
  included: boolean;
};

export type SelloutOfferQuote = {
  offerId: SelloutOfferId;
  title: string;
  eventIds: string[];
  token: string;
  totalIncVatPence: number;
  lines: SelloutOfferLine[];
  closesLabel: string;
};

export type StoredSelloutOffer = {
  offerId: SelloutOfferId;
  eventIds: string[];
  token: string;
};

export type SelloutPricedCourse = {
  eventId: string;
  key: string;
  title: string;
  priceExVatPence: number;
};

export class SelloutOfferError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "SelloutOfferError";
  }
}

type ChooseOffer = {
  id: "filler-pass" | "regenerative-pass";
  title: string;
  kind: "choose-3";
  keys: string[];
  closesAt: string | null;
  closesLabel: string;
  packageIncVatPence?: number;
};

type PairOffer = {
  id: "full-day-pass";
  title: string;
  kind: "pair";
  closesLabel: string;
};

type FixedOffer = {
  id: "skin-tech-duo" | "level-7-bls" | "trainer-week";
  title: string;
  kind: "fixed";
  keys: string[];
  includedKeys: string[];
  totalIncVatPence: number;
  closesAt: string | null;
  closesLabel: string;
};

export const FULL_DAY_PAIRS = [
  { id: "2026-10-22", label: "22 October", morning: "sclerotherapy-2026-10-22", afternoon: "vitamin-2026-10-22", date: "2026-10-22" },
  { id: "2026-11-07", label: "7 November", morning: "aqualyx-2026-11-07", afternoon: "lemon-bottle-2026-11-07", date: "2026-11-07" },
  { id: "2026-11-10", label: "10 November", morning: "cannula-2026-11-10", afternoon: "plla-2026-11-10", date: "2026-11-10" },
  { id: "2026-11-14", label: "14 November", morning: "boosters-2026-11-14", afternoon: "polynucleotides-2026-11-14", date: "2026-11-14" },
  { id: "2026-11-21", label: "21 November", morning: "peels-2026-11-21", afternoon: "lymphatic-2026-11-21", date: "2026-11-21" },
  { id: "2026-11-24", label: "24 November", morning: "tear-trough-2026-11-24", afternoon: "rhinoplasty-2026-11-24", date: "2026-11-24" },
  { id: "2026-11-27", label: "27 November", morning: "wood-therapy-2026-11-27", afternoon: "carbon-2026-11-27", date: "2026-11-27" },
  { id: "2026-11-28", label: "28 November", morning: "foundation-2026-11-28", afternoon: "advanced-anti-wrinkle-2026-11-28", date: "2026-11-28" },
  { id: "2026-12-12-rf-lymph", label: "12 December, RF and Cavitation with Lymphatic Drainage", morning: "rf-cavitation-2026-12-12", afternoon: "lymphatic-2026-12-12", date: "2026-12-12" },
  { id: "2026-12-12-rf-poly", label: "12 December, RF and Cavitation with Polynucleotides", morning: "rf-cavitation-2026-12-12", afternoon: "polynucleotides-2026-12-12", date: "2026-12-12" },
  { id: "2026-12-12-boost-lymph", label: "12 December, Skin Boosters with Lymphatic Drainage", morning: "boosters-2026-12-12", afternoon: "lymphatic-2026-12-12", date: "2026-12-12" },
  { id: "2026-12-12-boost-poly", label: "12 December, Skin Boosters with Polynucleotides", morning: "boosters-2026-12-12", afternoon: "polynucleotides-2026-12-12", date: "2026-12-12" },
] as const;

const CHOOSE_OFFERS: ChooseOffer[] = [
  {
    id: "filler-pass",
    title: "Filler Mastery Pass",
    kind: "choose-3",
    keys: ["8-point-2026-10-27", "lips-2026-10-28", "cannula-2026-11-10", "cheeks-2026-11-11", "tear-trough-2026-11-24", "rhinoplasty-2026-11-24"],
    closesAt: SELLOUT_OFFERS_CLOSES_AT,
    closesLabel: SELLOUT_OFFERS_CLOSES_LABEL,
    packageIncVatPence: 150_000,
  },
  {
    id: "regenerative-pass",
    title: "Regenerative Pass",
    kind: "choose-3",
    keys: ["prf-2026-11-03", "prp-2026-11-04", "plla-2026-11-10", "boosters-2026-11-14", "polynucleotides-2026-11-14", "boosters-2026-12-12", "polynucleotides-2026-12-12"],
    closesAt: SELLOUT_OFFERS_CLOSES_AT,
    closesLabel: SELLOUT_OFFERS_CLOSES_LABEL,
  },
];

const FIXED_OFFERS: FixedOffer[] = [
  {
    id: "skin-tech-duo",
    title: "Skin Tech Duo",
    kind: "fixed",
    keys: ["microneedling-2026-10-29", "hifu-2026-11-12"],
    includedKeys: ["peels-2026-11-21"],
    totalIncVatPence: 300_000,
    closesAt: SELLOUT_OFFERS_CLOSES_AT,
    closesLabel: SELLOUT_OFFERS_CLOSES_LABEL,
  },
  {
    id: "level-7-bls",
    title: "Level 7 + BLS",
    kind: "fixed",
    keys: ["level-7-2026-11-30"],
    includedKeys: ["bls-2026-11-28"],
    totalIncVatPence: 599_900,
    closesAt: SELLOUT_OFFERS_CLOSES_AT,
    closesLabel: SELLOUT_OFFERS_CLOSES_LABEL,
  },
  {
    id: "trainer-week",
    title: "Trainer and Assessor Week",
    kind: "fixed",
    keys: ["education-2026-11-24", "assessing-2026-11-25"],
    includedKeys: ["iqa-2026-11-27"],
    totalIncVatPence: 108_000,
    closesAt: SELLOUT_OFFERS_CLOSES_AT,
    closesLabel: SELLOUT_OFFERS_CLOSES_LABEL,
  },
];

export function isSelloutBannerVisible(at = new Date()) {
  const start = fromZonedTime("2026-10-20T00:00:00", UK_TIMEZONE);
  const end = fromZonedTime("2026-11-23T23:59:59", UK_TIMEZONE);
  return at >= start && at <= end;
}

export function selloutOfferClosesAt(offerId: SelloutOfferId, courseDate?: string) {
  if (offerId === "laser-career-bundle") {
    return fromZonedTime(SELLOUT_OFFERS_CLOSES_AT, UK_TIMEZONE);
  }
  if (offerId === "full-day-pass") {
    const campaignClose = fromZonedTime(SELLOUT_OFFERS_CLOSES_AT, UK_TIMEZONE);
    if (!courseDate) return campaignClose;
    const [year, month, day] = courseDate.split("-").map(Number);
    const previous = new Date(Date.UTC(year, month - 1, day));
    previous.setUTCDate(previous.getUTCDate() - 1);
    const key = previous.toISOString().slice(0, 10);
    const dateClose = fromZonedTime(`${key}T23:59:59`, UK_TIMEZONE);
    return dateClose < campaignClose ? dateClose : campaignClose;
  }
  const offer = [...CHOOSE_OFFERS, ...FIXED_OFFERS].find((item) => item.id === offerId);
  if (!offer?.closesAt) return null;
  return fromZonedTime(offer.closesAt, UK_TIMEZONE);
}

export function isSelloutOfferOpen(offerId: SelloutOfferId, at = new Date(), courseDate?: string) {
  const closes = selloutOfferClosesAt(offerId, courseDate);
  return !closes || at.getTime() <= closes.getTime();
}

export function formatOfferPence(pence: number) {
  return new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(pence / 100);
}

function sameIds(left: string[], right: string[]) {
  const a = [...left].sort();
  const b = [...right].sort();
  return a.length === b.length && a.every((id, index) => id === b[index]);
}

function lineFor(course: SelloutPricedCourse, chargedIncVatPence: number | null, included: boolean): SelloutOfferLine {
  return {
    eventId: course.eventId,
    title: course.title,
    catalogueIncVatPence: addVatPence(course.priceExVatPence),
    chargedIncVatPence,
    included,
  };
}

function requireCourses(courses: SelloutPricedCourse[], keys: string[], label: string) {
  if (new Set(courses.map((course) => course.eventId)).size !== courses.length) {
    throw new SelloutOfferError("Each date can only be added once.");
  }
  if (new Set(courses.map((course) => course.key)).size !== courses.length) {
    throw new SelloutOfferError("Each date can only be added once.");
  }
  const allowed = new Set(keys);
  if (courses.some((course) => !allowed.has(course.key))) {
    throw new SelloutOfferError(`${label} can only include its listed dates.`);
  }
}

export function priceSelloutOffer(offerId: SelloutOfferId, courses: SelloutPricedCourse[], at = new Date()): Omit<SelloutOfferQuote, "token"> {
  if (offerId === "laser-career-bundle") {
    const title = "Laser Career Bundle";
    if (!isSelloutOfferOpen(offerId, at)) {
      throw new SelloutOfferError(`${title} has closed.`);
    }
    const primary = courses.find((course) => {
      const dateKey = course.key.replace(/^laser-hair-removal-/, "");
      return laserCareerCourseKey({ title: course.title, dateKey }) === course.key;
    });
    if (!primary) {
      throw new SelloutOfferError("Choose a listed VTCT Laser Hair Removal date for this bundle.");
    }
    const expected = [primary.key, LASER_CARBON_KEY];
    requireCourses(courses, expected, title);
    if (!sameIds(courses.map((course) => course.key), expected)) {
      throw new SelloutOfferError(`${title} includes one VTCT Laser Hair Removal date and Carbon Laser Facials on 27 November.`);
    }
    const carbon = courses.find((course) => course.key === LASER_CARBON_KEY)!;
    const totalIncVatPence = addVatPence(primary.priceExVatPence);
    return {
      offerId,
      title,
      eventIds: [primary.eventId, carbon.eventId],
      totalIncVatPence,
      lines: [lineFor(primary, totalIncVatPence, false), lineFor(carbon, 0, true)],
      closesLabel: SELLOUT_OFFERS_CLOSES_LABEL,
    };
  }

  if (offerId === "filler-pass" || offerId === "regenerative-pass") {
    const offer = CHOOSE_OFFERS.find((item) => item.id === offerId)!;
    if (!isSelloutOfferOpen(offerId, at)) {
      throw new SelloutOfferError(`${offer.title} has closed.`);
    }
    requireCourses(courses, offer.keys, offer.title);
    if (courses.length !== 3) {
      throw new SelloutOfferError(`${offer.title} needs exactly 3 dates.`);
    }
    if (offer.packageIncVatPence) {
      return {
        offerId,
        title: offer.title,
        eventIds: courses.map((course) => course.eventId),
        totalIncVatPence: offer.packageIncVatPence,
        lines: courses.map((course) => lineFor(course, null, true)),
        closesLabel: offer.closesLabel,
      };
    }
    const lines = courses.map((course) => lineFor(course, addVatPence(course.priceExVatPence), false));
    let lowest = 0;
    lines.forEach((line, index) => {
      if ((line.catalogueIncVatPence) < lines[lowest].catalogueIncVatPence) lowest = index;
    });
    lines[lowest] = { ...lines[lowest], chargedIncVatPence: OFFER_COURSE_INC_VAT_PENCE };
    return {
      offerId,
      title: offer.title,
      eventIds: courses.map((course) => course.eventId),
      totalIncVatPence: lines.reduce((sum, line) => sum + (line.chargedIncVatPence || 0), 0),
      lines,
      closesLabel: offer.closesLabel,
    };
  }

  if (offerId === "full-day-pass") {
    if (courses.length !== 2) {
      throw new SelloutOfferError("A Full-Day Pass is one morning date and one afternoon date.");
    }
    const pair = FULL_DAY_PAIRS.find((item) => {
      const keys = [item.morning, item.afternoon];
      return courses.every((course) => keys.includes(course.key as typeof item.morning));
    });
    if (!pair || !sameIds(courses.map((course) => course.key), [pair.morning, pair.afternoon])) {
      throw new SelloutOfferError("Choose one of the listed same-day pairs.");
    }
    if (!isSelloutOfferOpen(offerId, at, pair.date)) {
      throw new SelloutOfferError(`The ${pair.label} Full-Day Pass has closed.`);
    }
    const morning = courses.find((course) => course.key === pair.morning)!;
    const afternoon = courses.find((course) => course.key === pair.afternoon)!;
    const lines = [
      lineFor(morning, addVatPence(morning.priceExVatPence), false),
      lineFor(afternoon, OFFER_COURSE_INC_VAT_PENCE, false),
    ];
    return {
      offerId,
      title: "Full-Day Pass",
      eventIds: [morning.eventId, afternoon.eventId],
      totalIncVatPence: lines.reduce((sum, line) => sum + (line.chargedIncVatPence || 0), 0),
      lines,
      closesLabel: selloutOfferClosesAt(offerId, pair.date)!.getTime() < fromZonedTime(SELLOUT_OFFERS_CLOSES_AT, UK_TIMEZONE).getTime()
        ? `Closes at 23:59 UK the day before ${pair.label}.`
        : SELLOUT_OFFERS_CLOSES_LABEL,
    };
  }

  const fixed = FIXED_OFFERS.find((item) => item.id === offerId);
  if (!fixed) {
    throw new SelloutOfferError("This offer is not available.");
  }
  if (!isSelloutOfferOpen(offerId, at)) {
    throw new SelloutOfferError(`${fixed.title} has closed.`);
  }
  const expected = [...fixed.keys, ...fixed.includedKeys];
  requireCourses(courses, expected, fixed.title);
  if (!sameIds(courses.map((course) => course.key), expected)) {
    throw new SelloutOfferError(`${fixed.title} includes a fixed set of dates.`);
  }
  const lines = courses.map((course) => lineFor(
    course,
    fixed.includedKeys.includes(course.key) ? 0 : null,
    true,
  ));
  return {
    offerId,
    title: fixed.title,
    eventIds: courses.map((course) => course.eventId),
    totalIncVatPence: fixed.totalIncVatPence,
    lines,
    closesLabel: fixed.closesLabel,
  };
}

export function chooseOfferKeys(offerId: "filler-pass" | "regenerative-pass") {
  return CHOOSE_OFFERS.find((offer) => offer.id === offerId)!.keys;
}

export function fixedOfferKeys(offerId: FixedOffer["id"]) {
  const offer = FIXED_OFFERS.find((item) => item.id === offerId)!;
  return { keys: offer.keys, includedKeys: offer.includedKeys, totalIncVatPence: offer.totalIncVatPence };
}

export function saveSelloutOffer(offer: StoredSelloutOffer) {
  sessionStorage.setItem(SELLOUT_OFFER_STORAGE_KEY, JSON.stringify(offer));
}

export function readSelloutOfferSelection(): StoredSelloutOffer | null {
  if (typeof window === "undefined") return null;
  try {
    const parsed = JSON.parse(sessionStorage.getItem(SELLOUT_OFFER_STORAGE_KEY) || "null");
    if (!parsed || !BOOKABLE_OFFER_IDS.includes(parsed.offerId) || !Array.isArray(parsed.eventIds) || typeof parsed.token !== "string") {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function clearSelloutOffer() {
  if (typeof window === "undefined") return;
  sessionStorage.removeItem(SELLOUT_OFFER_STORAGE_KEY);
}
